/**
 * MTO Cart Measurement Conflict — Shared Utility
 *
 * Handles detection and resolution of measurement conflicts for Made-to-Order
 * shirts in the Shopify cart. Used by both the product customisation page and
 * the account reorder flow.
 *
 * Exposed as: window.MtoCartConflict
 */
(function (global) {
  'use strict';

  // Fit fields that must be identical across all MTO items in the cart.
  // Frozen so external code cannot accidentally mutate the shared constant.
  const FIT_FIELDS = Object.freeze(['collar', 'chest', 'fit', 'sleeve']);

  // ---------------------------------------------------------------------------
  // Normalisation
  // ---------------------------------------------------------------------------

  /**
   * Normalise a measurement value to a canonical decimal string so that
   * "15½", "15 1/2", "15.5", and "15.50" all compare as equal.
   * Non-numeric values (e.g. fit type "regular") are lower-cased.
   *
   * @param {string|number|null|undefined} val
   * @returns {string}
   */
  const normalizeMeasurementValue = (val) => {
    if (val === null || val === undefined || val === '') return '';
    let s = String(val).trim();
    s = s.replace(/½/g, '.5').replace(/¼/g, '.25').replace(/¾/g, '.75');
    s = s.replace(/\s*1\/2/g, '.5').replace(/\s*1\/4/g, '.25').replace(/\s*3\/4/g, '.75');
    const num = parseFloat(s);
    if (!isNaN(num)) return String(num);
    return s.toLowerCase();
  };

  // ---------------------------------------------------------------------------
  // Cart helpers
  // ---------------------------------------------------------------------------

  /**
   * Fetch the cart and return only MTO line items — items that carry at least
   * one fit-measurement property.
   *
   * @returns {Promise<Array>}
   */
  const getMtoCartItems = async () => {
    const response = await fetch('/cart.js');
    const cart = await response.json();
    return (cart.items || []).filter(item => {
      const props = item.properties || {};
      return FIT_FIELDS.some(f => props[f] !== undefined && props[f] !== '');
    });
  };

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  /**
   * Check whether any MTO items currently in the cart have measurements that
   * differ from the supplied measurements map.
   *
   * @param {Object} currentMeasurements  e.g. { collar: '15.5', chest: '40', fit: 'regular', sleeve: '34' }
   * @returns {Promise<{ hasConflict: boolean, mtoItems: Array }>}
   */
  const checkCartMeasurementConflict = async (currentMeasurements) => {
    try {
      const mtoItems = await getMtoCartItems();
      if (!mtoItems.length) return { hasConflict: false, mtoItems: [] };

      const measurements = currentMeasurements || {};
      const hasConflict = mtoItems.some(item => {
        const props = item.properties || {};
        return FIT_FIELDS.some(f => {
          const cartVal = normalizeMeasurementValue(props[f] || '');
          const newVal  = normalizeMeasurementValue(measurements[f] || '');
          return cartVal && newVal && cartVal !== newVal;
        });
      });

      return { hasConflict, mtoItems };
    } catch (err) {
      console.warn('[MtoCartConflict] Could not check cart:', err);
      return { hasConflict: false, mtoItems: [] };
    }
  };

  /**
   * Display the measurement-conflict confirmation modal.
   * The modal HTML is rendered by snippets/mto-conflict-modal.liquid — this
   * function only handles show/hide and resolves the customer's choice.
   *
   * Focus moves into the modal on open and is restored to the triggering
   * element on close. Escape key cancels.
   *
   * @returns {Promise<boolean>}  true = customer confirmed, false = cancelled
   */
  const showMeasurementConflictModal = () => {
    return new Promise((resolve) => {
      const modal = document.getElementById('hamilton-measurement-conflict-modal');
      if (!modal) {
        // Snippet not rendered on this page — fail open so add-to-cart proceeds.
        console.warn('[MtoCartConflict] Modal element not found. Ensure mto-conflict-modal snippet is rendered.');
        resolve(true);
        return;
      }

      // Capture focus so we can restore it when the modal closes.
      const previousFocus = document.activeElement;
      modal.style.display = 'flex';

      // Move focus into the modal. Cancel is the safe default — prevents an
      // accidental Enter-key confirmation.
      modal.querySelector('#hmc-cancel')?.focus();

      // AbortController lets us tear down all listeners in one call regardless
      // of which path (confirm / cancel / backdrop / Escape) closes the modal.
      const controller = new AbortController();
      const { signal } = controller;

      const close = (result) => {
        controller.abort();
        modal.style.display = 'none';
        if (previousFocus && typeof previousFocus.focus === 'function') {
          previousFocus.focus();
        }
        resolve(result);
      };

      modal.querySelector('#hmc-confirm').addEventListener('click', () => close(true),  { signal });
      modal.querySelector('#hmc-cancel').addEventListener('click',  () => close(false), { signal });
      modal.addEventListener('click', (e) => { if (e.target === modal) close(false); },  { signal });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(false); }, { signal });
    });
  };

  /**
   * Overwrite the fit-measurement properties of the given MTO cart items to
   * match newMeasurements. All other properties on each item are preserved.
   * Updates are applied sequentially to avoid Shopify cart race conditions.
   *
   * All items are attempted regardless of individual failures. If any fail,
   * an Error is thrown after all attempts so the caller can surface it to
   * the customer — a partial update without user feedback is worse than
   * a visible error asking them to refresh.
   *
   * @param {Array}  mtoItems         Cart line items from /cart.js
   * @param {Object} newMeasurements  { collar, chest, fit, sleeve }
   * @returns {Promise<void>}
   * @throws {Error} if one or more cart items could not be updated
   */
  const updateCartMeasurements = async (mtoItems, newMeasurements) => {
    const measurements = newMeasurements || {};
    const failures = [];

    for (const item of mtoItems) {
      const updatedProperties = { ...item.properties };
      FIT_FIELDS.forEach(f => {
        if (measurements[f] !== undefined) updatedProperties[f] = measurements[f];
      });
      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: item.key, properties: updatedProperties }),
        });
      } catch (err) {
        failures.push(item.key);
        console.warn('[MtoCartConflict] Failed to update cart item:', item.key, err);
      }
    }

    if (failures.length > 0) {
      throw new Error(
        'Unable to update measurements on ' + failures.length +
        ' cart item' + (failures.length > 1 ? 's' : '') +
        '. Please refresh the page and try again.'
      );
    }
  };

  // ---------------------------------------------------------------------------
  // Expose
  // ---------------------------------------------------------------------------
  global.MtoCartConflict = Object.freeze({
    FIT_FIELDS,
    normalizeMeasurementValue,
    checkCartMeasurementConflict,
    showMeasurementConflictModal,
    updateCartMeasurements,
  });

}(window));
